import type { TimeSignature } from './ecoRitmoConfig';
import type { TrainSpriteSlot } from './trainSprites';

export const TIME_SIGNATURES: TimeSignature[] = ['2/4', '3/4', '4/4'];
export const MEASURE_COUNTS = [1, 2, 3, 4] as const;
export type MeasureCount = (typeof MEASURE_COUNTS)[number];

export interface EcoSlotTuning {
  offsetTop: number;
  offsetLeft: number;
  widthScale: number;
  heightScale: number;
}

/** Layout del tren para una combinación compás × nº de compases. */
export interface EcoLayoutPreset {
  trainScale: number;
  trainHeightMult: number;
  rowGapRem: number;
  colGapRem: number;
  singleRowMaxPx: number;
  figureScale: number;
}

/** Clave: `4/4@3` = compás 4/4 con 3 compases visibles. */
export type EcoPresetKey = `${TimeSignature}@${MeasureCount}`;

export interface EcoLayoutTuningStore {
  version: 2;
  /** Ventanas calibradas por sprite (compás musical). */
  slots: Record<TimeSignature, EcoSlotTuning>;
  /** Escala y gaps por compás × cantidad de compases. */
  presets: Record<EcoPresetKey, EcoLayoutPreset>;
}

export const DEFAULT_SLOT_TUNING: EcoSlotTuning = {
  offsetTop: 0,
  offsetLeft: 0,
  widthScale: 1,
  heightScale: 1,
};

export const DEFAULT_LAYOUT_PRESET: EcoLayoutPreset = {
  trainScale: 1,
  trainHeightMult: 1,
  rowGapRem: 0.35,
  colGapRem: 0.85,
  singleRowMaxPx: 920,
  figureScale: 1.5,
};

/** Defaults sugeridos según cuántos compases hay en pantalla. */
const PRESET_HINTS_BY_COUNT: Record<MeasureCount, Partial<EcoLayoutPreset>> = {
  1: { trainScale: 1.08, trainHeightMult: 1.1, singleRowMaxPx: 980, rowGapRem: 0.35, figureScale: 1.42 },
  2: { trainScale: 1, trainHeightMult: 1, singleRowMaxPx: 920, rowGapRem: 0.35 },
  3: { trainScale: 0.92, trainHeightMult: 0.96, singleRowMaxPx: 780, rowGapRem: 0.28, colGapRem: 0.75 },
  4: { trainScale: 0.84, trainHeightMult: 0.9, singleRowMaxPx: 700, rowGapRem: 0.22, colGapRem: 0.65 },
};

const STORAGE_KEY = 'edusico-junior-eco-layout-tuning';

export function presetKey(ts: TimeSignature, measureCount: number): EcoPresetKey {
  const n = Math.min(4, Math.max(1, Math.round(measureCount))) as MeasureCount;
  return `${ts}@${n}`;
}

export function allPresetKeys(): EcoPresetKey[] {
  return TIME_SIGNATURES.flatMap((ts) =>
    MEASURE_COUNTS.map((n) => presetKey(ts, n))
  );
}

export function presetLabel(key: EcoPresetKey): string {
  const [ts, n] = key.split('@') as [TimeSignature, string];
  return `${ts} · ${n} compás${n === '1' ? '' : 'es'}`;
}

function buildDefaultPresets(): Record<EcoPresetKey, EcoLayoutPreset> {
  const presets = {} as Record<EcoPresetKey, EcoLayoutPreset>;
  for (const key of allPresetKeys()) {
    const count = Number(key.split('@')[1]) as MeasureCount;
    presets[key] = { ...DEFAULT_LAYOUT_PRESET, ...PRESET_HINTS_BY_COUNT[count] };
  }
  return presets;
}

export const DEFAULT_ECO_LAYOUT_STORE: EcoLayoutTuningStore = {
  version: 2,
  slots: {
    '2/4': { ...DEFAULT_SLOT_TUNING },
    '3/4': { ...DEFAULT_SLOT_TUNING },
    '4/4': { ...DEFAULT_SLOT_TUNING },
  },
  presets: buildDefaultPresets(),
};

function parsePct(value: string): number {
  return Number.parseFloat(value) || 0;
}

function fmtPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

/** Migra formato v1 (global único) al store v2. */
function migrateLegacy(raw: unknown): EcoLayoutTuningStore | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.version === 2) return o as EcoLayoutTuningStore;
  if (!('trainScale' in o)) return null;

  const legacy = o as {
    trainScale?: number;
    trainHeightMult?: number;
    rowGapRem?: number;
    colGapRem?: number;
    singleRowMaxPx?: number;
    figureScale?: number;
    slots?: Record<TimeSignature, EcoSlotTuning>;
  };

  const basePreset: EcoLayoutPreset = {
    ...DEFAULT_LAYOUT_PRESET,
    trainScale: legacy.trainScale ?? DEFAULT_LAYOUT_PRESET.trainScale,
    trainHeightMult: legacy.trainHeightMult ?? DEFAULT_LAYOUT_PRESET.trainHeightMult,
    rowGapRem: legacy.rowGapRem ?? DEFAULT_LAYOUT_PRESET.rowGapRem,
    colGapRem: legacy.colGapRem ?? DEFAULT_LAYOUT_PRESET.colGapRem,
    singleRowMaxPx: legacy.singleRowMaxPx ?? DEFAULT_LAYOUT_PRESET.singleRowMaxPx,
    figureScale: legacy.figureScale ?? DEFAULT_LAYOUT_PRESET.figureScale,
  };

  const presets = buildDefaultPresets();
  for (const key of allPresetKeys()) {
    presets[key] = { ...presets[key], ...basePreset };
  }

  return {
    version: 2,
    slots: {
      '2/4': { ...DEFAULT_SLOT_TUNING, ...legacy.slots?.['2/4'] },
      '3/4': { ...DEFAULT_SLOT_TUNING, ...legacy.slots?.['3/4'] },
      '4/4': { ...DEFAULT_SLOT_TUNING, ...legacy.slots?.['4/4'] },
    },
    presets,
  };
}

function mergeStore(parsed: Partial<EcoLayoutTuningStore>): EcoLayoutTuningStore {
  const presets = buildDefaultPresets();
  for (const key of allPresetKeys()) {
    presets[key] = { ...presets[key], ...parsed.presets?.[key] };
  }
  return {
    version: 2,
    slots: {
      '2/4': { ...DEFAULT_SLOT_TUNING, ...parsed.slots?.['2/4'] },
      '3/4': { ...DEFAULT_SLOT_TUNING, ...parsed.slots?.['3/4'] },
      '4/4': { ...DEFAULT_SLOT_TUNING, ...parsed.slots?.['4/4'] },
    },
    presets,
  };
}

export function loadEcoLayoutTuning(): EcoLayoutTuningStore {
  if (typeof window === 'undefined') return DEFAULT_ECO_LAYOUT_STORE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_ECO_LAYOUT_STORE;
    const parsed = JSON.parse(raw) as unknown;
    const migrated = migrateLegacy(parsed);
    if (migrated) return mergeStore(migrated);
    return DEFAULT_ECO_LAYOUT_STORE;
  } catch {
    return DEFAULT_ECO_LAYOUT_STORE;
  }
}

export function saveEcoLayoutTuning(store: EcoLayoutTuningStore): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function getPreset(
  store: EcoLayoutTuningStore,
  ts: TimeSignature,
  measureCount: number
): EcoLayoutPreset {
  const key = presetKey(ts, measureCount);
  return store.presets[key] ?? DEFAULT_LAYOUT_PRESET;
}

export function presetToCssVars(preset: EcoLayoutPreset): Record<string, string> {
  return {
    '--eco-train-scale': String(preset.trainScale),
    '--eco-train-height-mult': String(preset.trainHeightMult),
    '--eco-row-gap': `${preset.rowGapRem}rem`,
    '--eco-col-gap': `${preset.colGapRem}rem`,
    '--eco-single-max-w': `${preset.singleRowMaxPx}px`,
    '--eco-figure-scale': String(preset.figureScale),
  };
}

export function cssVarsForSession(
  store: EcoLayoutTuningStore,
  ts: TimeSignature,
  measureCount: number
): Record<string, string> {
  return presetToCssVars(getPreset(store, ts, measureCount));
}

export function applySlotTuning(
  layout: TrainSpriteSlot,
  tuning: EcoSlotTuning
): TrainSpriteSlot {
  const left = parsePct(layout.left) + tuning.offsetLeft;
  const top = parsePct(layout.top) + tuning.offsetTop;
  const width = parsePct(layout.width) * tuning.widthScale;
  const height = parsePct(layout.height) * tuning.heightScale;
  return {
    left: fmtPct(left),
    top: fmtPct(top),
    width: fmtPct(width),
    height: fmtPct(height),
  };
}

export function exportSlotsForConfig(
  baseSlots: TrainSpriteSlot[],
  tuning: EcoSlotTuning
): TrainSpriteSlot[] {
  return baseSlots.map((s) => applySlotTuning(s, tuning));
}

export function tuningExportJson(store: EcoLayoutTuningStore): string {
  return JSON.stringify(store, null, 2);
}

/** Resumen legible para commit en código. */
export function exportActiveConfig(
  store: EcoLayoutTuningStore,
  ts: TimeSignature,
  measureCount: number
): object {
  const key = presetKey(ts, measureCount);
  const base = store.presets[key];
  return {
    presetKey: key,
    layout: base,
    slots: store.slots[ts],
    slotsResolved: exportSlotsForConfig(
      [], // caller can merge with TRAIN_SPRITES
      store.slots[ts]
    ),
  };
}
